import { closeIcon, saveIcon } from "assets/images";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  PERSONAL_SMART_GOALS_MAX_LENGTH,
  goalsTextFieldConfigs,
} from "utils/constants/slp";
import {
  getImportanceError,
  getDifficultyError,
} from "utils/helper/validation-message-helper";
import { useForm } from "react-hook-form";
import { IAddPersonalSmartGoals } from "utils/interfaces/slp-form";
import { toast } from "react-toastify";
import {
  AddPersonalAndSmartGoalsPayload,
  AddPersonalAndSmartGoalsSuccessPayload,
} from "store/slpForm/types";
import {
  NO_NEXT_OR_CURRENT_SLP_FOUND,
  PERSONAL_SMART_GOALS_ADDED_SUCCESS_MSG,
  REQUIRED_MARK,
} from "utils/constants/Messages";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import RenderRadioGroup from "components/RenderRadioGroup";

interface IAddSLPGoalsModalProps {
  nextOrCurrentSLPId: number;
  openGoalsModal: boolean;
  setOpenGoalsModal: React.Dispatch<React.SetStateAction<boolean>>;
  addPersonalAndSmartGoalsRequest: (
    payload: AddPersonalAndSmartGoalsPayload
  ) => {};
  updateGoalsListCallback: () => void;
}

const AddSLPGoalsModal: React.FC<IAddSLPGoalsModalProps> = (props) => {
  const {
    openGoalsModal,
    setOpenGoalsModal,
    nextOrCurrentSLPId,
    addPersonalAndSmartGoalsRequest,
    updateGoalsListCallback,
  } = props;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IAddPersonalSmartGoals>({
    defaultValues: {
      studentGoals: "",
      smartGoals: "",
      baselineLevel: "",
      importance: 0,
      difficulty: 0,
    },
  });

  const handleClose = () => {
    setOpenGoalsModal(false);
  };

  const onPersonalAndSmartGoalsSuccess = (
    response: AddPersonalAndSmartGoalsSuccessPayload
  ) => {
    if (response.success) {
      hideLoaderForBatchApiCall();
      reset();
      toast.success(PERSONAL_SMART_GOALS_ADDED_SUCCESS_MSG);
      handleClose();
    }
    updateGoalsListCallback();
  };

  const onSubmit = (data: IAddPersonalSmartGoals) => {
    if (addPersonalAndSmartGoalsRequest && nextOrCurrentSLPId) {
      showLoaderForBatchApiCall();
      data.slpId = nextOrCurrentSLPId;
      const payload = {
        values: {
          ...data,
          studentGoals: data.studentGoals.trim(),
          smartGoals: data.smartGoals.trim(),
          baselineLevel: data.baselineLevel.trim(),
        },
        callback: onPersonalAndSmartGoalsSuccess,
      };
      addPersonalAndSmartGoalsRequest(payload);
    } else {
      toast.error(NO_NEXT_OR_CURRENT_SLP_FOUND);
    }
  };

  return (
    <Modal open={openGoalsModal} onClose={handleClose}>
      <Box className="common-modal modal-600">
        <Box className="modal-header">
          <Typography variant="h4">Add Goals To Current SLP</Typography>
          <IconButton onClick={handleClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className="modal-body">
            <Grid container columnSpacing="16px" sx={{ mb: 2 }}>
              {goalsTextFieldConfigs(errors).map(
                ({ name, label, error, helper }) => (
                  <Grid item xs={12} key={name}>
                    <TextField
                      multiline
                      fullWidth
                      label={
                        <>
                          {label} {REQUIRED_MARK}
                        </>
                      }
                      variant="outlined"
                      className="suggestion-textarea"
                      rows={3}
                      error={!!error}
                      helperText={helper}
                      InputLabelProps={{
                        shrink: Boolean(
                          watch(name as keyof IAddPersonalSmartGoals)
                        ),
                      }}
                      {...register(name as keyof IAddPersonalSmartGoals, {
                        required: true,
                        maxLength: PERSONAL_SMART_GOALS_MAX_LENGTH,
                      })}
                    />
                  </Grid>
                )
              )}

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <RenderRadioGroup
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  name="importance"
                  label="Importance"
                  errorMsg={getImportanceError(errors.importance)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <RenderRadioGroup
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  name="difficulty"
                  label="Difficulty"
                  errorMsg={getDifficultyError(errors.difficulty)}
                />
              </Grid>
            </Grid>
          </Box>
          <Box className="modal-footer btn-save">
            <Button variant="contained" className="btn-save" type="submit">
              <img src={saveIcon} alt="save" />
              Save
            </Button>
            <Button
              variant="outlined"
              className="btn-cancel"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default AddSLPGoalsModal;
